import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { completedRows, formatDuration, formatPace, gramsToKg, paceSecondsPerKm, toKilogramReps } from '@liftx/core';
import { Screen } from '../../ui/components/Screen';
import { sessionRepository, type WorkoutSummary as WorkoutSummaryData } from '../../data/repositories/sessionRepository';
import { exerciseRepository } from '../../data/repositories/exerciseRepository';
import { formatExerciseName } from '../../ui/format/exerciseName';
import { HistoryExerciseThumbnail } from '../../ui/components/ExerciseThumbnail';
import { ROUTES } from '../../ui/navigation/routes';
import type {
  CardioSegmentRecord,
  ExerciseRecord,
  SessionBlockRecord,
  SessionExerciseRecord,
  SessionSetRecord,
} from '../../data/types';
import styles from './Workout.module.css';

const PR_LABELS: Record<string, string> = {
  rep_max: 'Rep max',
  est_1rm: 'Estimated 1RM',
  max_weight: 'Max weight',
  max_volume_set: 'Best set volume',
  max_reps: 'Max reps',
  min_assistance: 'Least assistance',
  max_duration: 'Max duration',
  max_distance: 'Max distance',
  best_pace: 'Best pace',
};

interface PerformedExercise {
  exercise: SessionExerciseRecord;
  sets: SessionSetRecord[];
  /** Empty for a strength/hold exercise. */
  segments: CardioSegmentRecord[];
}

interface PerformedBlock {
  block: SessionBlockRecord;
  exercises: PerformedExercise[];
}

/** Metres -> "5.00 km" — same convention as History's own local helper. */
function formatDistance(distanceM: number | null): string {
  if (distanceM === null) return '—';
  return `${(distanceM / 1000).toFixed(2)} km`;
}

/**
 * One recorded set, read-only.
 *
 * Shows only the values actually recorded — `weightG`/`reps`/`durationS` are
 * each rendered only when not `null`, never coerced to `0`. That is what
 * keeps a bodyweight set from ever printing "0 kg": nothing writes a real 0
 * into `weightG` for a bodyweight rep, so a `null` there already means
 * "no weight recorded," the same convention `HistoryDetailScreen`'s
 * `SetDetailRow` uses for the exact same fields.
 */
function PerformedSetRow({ set, index }: { set: SessionSetRecord; index: number }): React.JSX.Element {
  const parts: string[] = [];
  if (set.reps !== null) parts.push(`${String(set.reps)} reps`);
  if (set.weightG !== null) parts.push(`${String(gramsToKg(set.weightG))} kg`);
  if (set.durationS !== null) parts.push(formatDuration(set.durationS));

  return (
    <li className={styles.summarySetRow}>
      <span>Set {index + 1}</span>
      <span>{parts.length > 0 ? parts.join(' — ') : '—'}</span>
    </li>
  );
}

/** One recorded cardio segment, read-only — same fields and derivation `CardioSegmentDetailRow` (History) uses. */
function PerformedSegmentRow({ segment, index }: { segment: CardioSegmentRecord; index: number }): React.JSX.Element {
  const pace = paceSecondsPerKm(segment.distanceM, segment.durationS);

  return (
    <li className={styles.summarySetRow}>
      <span>Segment {index + 1}</span>
      <span>
        {segment.durationS !== null ? formatDuration(segment.durationS) : '—'}
        {' · '}
        {formatDistance(segment.distanceM)}
        {' · '}
        {pace.known ? formatPace(pace.value) : '—'}
        {segment.avgHeartRate !== null && ` · ${String(segment.avgHeartRate)} bpm`}
        {segment.resistanceLevel != null && ` · Level ${String(segment.resistanceLevel)}`}
        {segment.inclinePct != null && ` · Incline ${String(segment.inclinePct)}%`}
        {segment.estimatedSpeedKph != null && ` · Est. ${segment.estimatedSpeedKph.toFixed(1)} km/h`}
      </span>
    </li>
  );
}

/**
 * One performed exercise: a large, clearly-identifiable image, its name, and
 * every set/segment actually recorded for it.
 *
 * The image is the session's own frozen snapshot (`HistoryExerciseThumbnail`)
 * — what was current when the workout was recorded, not a live lookup — sized
 * well past the 44–96px thumbnail used everywhere else this component
 * appears, via `summaryImage`'s `!important` override (see that component's
 * `className` doc comment for why `!important` is the only way to win here).
 */
function PerformedExercisePanel({
  performed,
  catalogueExerciseId,
}: {
  performed: PerformedExercise;
  catalogueExerciseId: string | null;
}): React.JSX.Element {
  const { exercise, sets, segments } = performed;

  return (
    <div className={styles.exercisePanel}>
      <div className={styles.summaryImageWrap}>
        <HistoryExerciseThumbnail
          mediaIdsSnapshot={exercise.mediaIdsSnapshot}
          catalogueExerciseId={catalogueExerciseId}
          size={200}
          className={styles.summaryImage}
        />
      </div>

      <p className={`${styles.exerciseName} ${styles.summaryExerciseName}`}>
        {formatExerciseName(exercise.exerciseNameSnapshot)}
      </p>

      {/* Data-driven, not modality-gated: a cardio exercise has segments and
          zero sets, everything else has sets and zero segments — same rule
          `HistoryDetailScreen` uses for the identical fields. */}
      <ul className={styles.setList}>
        {sets.map((set, index) => (
          <PerformedSetRow key={set.id} set={set} index={index} />
        ))}
        {segments.map((segment, index) => (
          <PerformedSegmentRow key={segment.id} segment={segment} index={index} />
        ))}
      </ul>
    </div>
  );
}

/**
 * The immediate post-workout summary. Master Phase 6.
 *
 * Aggregate stats are fully recomputed from persisted data via
 * `sessionRepository.getSummary()` — reachable and correct on a fresh page
 * load, not dependent on having just come from `complete()`. This is the one
 * piece of "History" Phase 6 owns (§49 of the approved plan): a single
 * immediate view, not a browsable list.
 *
 * The per-exercise "Workout Summary" section (2026-09-15) loads the same
 * block/exercise/set/segment rows `HistoryDetailScreen` already loads for a
 * past session — `getSummary()` itself only returns aggregate totals, so the
 * per-set detail this section needs was never discarded anywhere; it simply
 * was not being fetched by this screen.
 */
export function WorkoutSummaryScreen(): React.JSX.Element {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [summary, setSummary] = useState<WorkoutSummaryData | null | undefined>(undefined);
  const [blocks, setBlocks] = useState<PerformedBlock[]>([]);
  const [exerciseSources, setExerciseSources] = useState<Record<string, ExerciseRecord['source']>>({});
  const [exportingReport, setExportingReport] = useState(false);
  const [sharingReport, setSharingReport] = useState(false);
  const [exportMessage, setExportMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (sessionId === undefined) return;
    const result = await sessionRepository.getSummary(sessionId);
    setSummary(result);
    if (result === null) return;

    const blockRows = await sessionRepository.listBlocks(sessionId);
    const withExercises = await Promise.all(
      blockRows.map(async (block) => {
        const exerciseRows = await sessionRepository.listExercises(block.id);
        const loaded = await Promise.all(
          exerciseRows.map(async (exercise) => ({
            exercise,
            // Performed work only — same rule as History detail. The live
            // workout screen keeps using the unfiltered repository lists.
            sets: completedRows(await sessionRepository.listSets(exercise.id)),
            segments: completedRows(await sessionRepository.listSegments(exercise.id)),
          })),
        );
        // Nothing completed (untouched, or a legacy `'skipped'` row with no
        // work) -> the exercise is not part of what was performed.
        const exercises = loaded.filter(({ sets, segments }) => sets.length > 0 || segments.length > 0);
        return { block, exercises };
      }),
    );
    setBlocks(withExercises);

    // Only the live exercise's `source` is consulted (for the catalogue-GIF
    // fallback when the session's own image snapshot is empty) — never its
    // mutable media fields. Same rule `HistoryDetailScreen` follows for the
    // identical lookup.
    const uniqueExerciseIds = [
      ...new Set(withExercises.flatMap(({ exercises }) => exercises.map(({ exercise }) => exercise.exerciseId))),
    ].filter((exerciseId): exerciseId is string => exerciseId !== null);
    const rows = await Promise.all(uniqueExerciseIds.map((exerciseId) => exerciseRepository.get(exerciseId)));
    setExerciseSources(
      Object.fromEntries(
        rows.filter((row): row is NonNullable<typeof row> => row !== undefined).map((row) => [row.id, row.source]),
      ),
    );
  }, [sessionId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleExportReport(): Promise<void> {
    if (sessionId === undefined || exportingReport) return;
    setExportingReport(true);
    setExportMessage(null);
    try {
      // jsPDF is a sizeable dependency; `ActiveWorkoutScreen`/`WorkoutSummaryScreen`
      // stay eager per `App.tsx`'s own routing rule, so pulling it in only when
      // Export is actually pressed keeps it out of every session's initial load.
      const [{ buildWorkoutReportData }, { downloadWorkoutReportPdf }] = await Promise.all([
        import('../../data/reports/workoutReport'),
        import('../../data/reports/workoutReportPdf'),
      ]);
      const data = await buildWorkoutReportData(sessionId);
      if (data === null) {
        setExportMessage('This workout could not be found.');
        return;
      }
      const fileName = await downloadWorkoutReportPdf(data);
      setExportMessage(`Exported to ${fileName}.`);
    } catch (error) {
      setExportMessage(error instanceof Error ? `Export failed: ${error.message}` : 'Export failed.');
    } finally {
      setExportingReport(false);
    }
  }

  async function handleShareReport(): Promise<void> {
    if (sessionId === undefined || sharingReport) return;
    setSharingReport(true);
    setExportMessage(null);
    try {
      const [{ buildWorkoutReportData }, { shareWorkoutReportPdf }] = await Promise.all([
        import('../../data/reports/workoutReport'),
        import('../../data/reports/workoutReportPdf'),
      ]);
      const data = await buildWorkoutReportData(sessionId);
      if (data === null) {
        setExportMessage('This workout could not be found.');
        return;
      }
      const result = await shareWorkoutReportPdf(data);
      setExportMessage(result.shared ? `Shared ${result.fileName}.` : `Sharing isn't supported here — downloaded ${result.fileName} instead.`);
    } catch (error) {
      setExportMessage(error instanceof Error ? `Share failed: ${error.message}` : 'Share failed.');
    } finally {
      setSharingReport(false);
    }
  }

  if (summary === undefined) {
    return (
      <p role="status" className={styles.stateText}>
        Loading summary…
      </p>
    );
  }
  if (summary === null) {
    return (
      <p role="status" className={styles.stateText}>
        This workout could not be found.
      </p>
    );
  }

  return (
    <Screen title={summary.session.name ?? 'Workout complete'} subtitle="Nice work.">
      <div className={styles.state}>
        <div className={styles.summaryStat}>
          <span>Duration:</span>
          <span>{formatDuration(summary.stats.durationS)}</span>
        </div>
        <div className={styles.summaryStat}>
          <span>Sets completed:</span>
          <span>{summary.stats.totalSets}</span>
        </div>
        <div className={styles.summaryStat}>
          <span>Total reps:</span>
          <span>{summary.stats.totalReps}</span>
        </div>
        <div className={styles.summaryStat}>
          <span>Volume:</span>
          <span>{toKilogramReps(summary.stats.totalVolumeGramReps).toLocaleString()} kg-reps</span>
        </div>
      </div>

      {/* Cardio Phase C6 — null for a strength/hold-only workout, so this
          screen is unchanged for every workout that isn't cardio. Same
          `summary.stats.cardio` block History shows, kept in sync
          deliberately: both read the identical `WorkoutSummary`.

          Gated on `completedSegments > 0`, not merely `!== null`: a
          materialised-but-never-touched segment (C3 seeds one from the plan
          target) would otherwise render a 'Cardio' block that is nothing but
          em dashes — exactly the 'prescription presented as performance'
          shape this whole feature exists to avoid. No real performance, no
          block. */}
      {summary.stats.cardio !== null && summary.stats.cardio.completedSegments > 0 && (
        <div className={styles.state}>
          <div className={styles.summaryStat}>
            <span>Cardio duration:</span>
            <span>
              {summary.stats.cardio.totalDurationS > 0 ? formatDuration(summary.stats.cardio.totalDurationS) : '—'}
            </span>
          </div>
          <div className={styles.summaryStat}>
            <span>Cardio distance:</span>
            <span>
              {summary.stats.cardio.totalDistanceM > 0
                ? `${(summary.stats.cardio.totalDistanceM / 1000).toFixed(2)} km`
                : '—'}
            </span>
          </div>
          <div className={styles.summaryStat}>
            <span>Average pace:</span>
            <span>{summary.stats.cardio.pace.known ? formatPace(summary.stats.cardio.pace.value) : '—'}</span>
          </div>
          <div className={styles.summaryStat}>
            <span>Average heart rate:</span>
            <span>{summary.stats.cardio.averageHr === null ? '—' : `${String(summary.stats.cardio.averageHr)} bpm`}</span>
          </div>
        </div>
      )}

      {blocks.some(({ exercises }) => exercises.length > 0) && (
        <section aria-labelledby="workout-summary-heading">
          <h2 id="workout-summary-heading" className={styles.stateTitle}>
            Workout Summary
          </h2>
          {blocks.map(({ block, exercises }) => (
            <div key={block.id}>
              {exercises.map((performed) => (
                <PerformedExercisePanel
                  key={performed.exercise.id}
                  performed={performed}
                  catalogueExerciseId={
                    performed.exercise.exerciseId !== null &&
                    exerciseSources[performed.exercise.exerciseId] === 'exercisedb'
                      ? performed.exercise.exerciseId
                      : null
                  }
                />
              ))}
            </div>
          ))}
        </section>
      )}

      {summary.achievedRecords.length > 0 && (
        <div className={styles.state} role="status">
          <p className={styles.stateTitle}>Personal records</p>
          {summary.achievedRecords.map((record) => (
            <div key={record.id} className={styles.prRow}>
              <span className={`${styles.badge} ${styles.badgeAccent}`}>PR</span>
              <span>
                {PR_LABELS[record.kind] ?? record.kind}
                {record.repCount !== null ? ` @ ${String(record.repCount)} reps` : ''}:{' '}
                {record.unit === 'g' ? `${String(gramsToKg(record.value))} kg` : `${String(record.value)} ${record.unit}`}
              </span>
            </div>
          ))}
        </div>
      )}

      {summary.progressionSuggestions.length > 0 && (
        <div className={styles.state} role="status">
          <p className={styles.stateTitle}>Next session</p>
          {summary.progressionSuggestions.map((entry) => (
            <p key={entry.sessionExerciseId} className={styles.stateText}>
              {entry.suggestion.explanation}
            </p>
          ))}
        </div>
      )}

      {exportMessage !== null && (
        <p className={styles.stateText} role="status">
          {exportMessage}
        </p>
      )}

      <div className={styles.actions}>
        <button type="button" className={styles.actionButton} onClick={() => void handleExportReport()} disabled={exportingReport}>
          {exportingReport ? 'Exporting…' : 'Export PDF'}
        </button>
        <button type="button" className={styles.actionButton} onClick={() => void handleShareReport()} disabled={sharingReport}>
          {sharingReport ? 'Sharing…' : 'Share PDF'}
        </button>
        <Link className={`${styles.actionButton} ${styles.actionOn}`} to={ROUTES.workout}>
          Done
        </Link>
      </div>
    </Screen>
  );
}
